import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import CodeMirror from "@uiw/react-codemirror";
import { javascript } from "@codemirror/lang-javascript";
import { oneDark } from "@codemirror/theme-one-dark";
import { githubLight } from "@uiw/codemirror-theme-github";
import {
  DndContext, PointerSensor, closestCenter, useSensor, useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext, arrayMove, useSortable, verticalListSortingStrategy,
} from "@dnd-kit/sortable";

import {
  api,
  type Flow,
  type FlowRun,
  type FlowRunStep,
  type FlowStep,
  type RequestSummary,
} from "../lib/api";
import { InlineLoading, LoadingButton } from "./Spinner";
import { useConfirm } from "./ConfirmDialog";
import CodeViewer from "./CodeViewer";
import Markdown from "./Markdown";
import { useResizable } from "../lib/useResizable";
import { closeOpenTabFor } from "../lib/openTabs";

type Tab = "step" | "run" | "history" | "docs";

interface RunOutcome { run: FlowRun; steps: FlowRunStep[] }

export default function FlowBuilder({ flowId, workspaceId }: { flowId: string; workspaceId: string }) {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const detailQ = useQuery({ queryKey: ["flow", flowId], queryFn: () => api.flows.get(flowId) });
  const data = detailQ.data?.flow ?? null;
  const envsQ = useQuery({ queryKey: ["environments", workspaceId], queryFn: () => api.environments.list(workspaceId) });

  const [name, setName] = useState("");
  const [docs, setDocs] = useState("");
  const [steps, setSteps] = useState<FlowStep[]>([]);
  const [selectedStepId, setSelectedStepId] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  const [sp, setSp] = useSearchParams();
  const tab: Tab = ((): Tab => {
    const q = sp.get("tab") ?? "";
    const valid: Tab[] = ["step", "run", "history", "docs"];
    return (valid as string[]).includes(q) ? (q as Tab) : "step";
  })();
  const setTab = (t: Tab) => {
    const next = new URLSearchParams(sp);
    if (t === "step") next.delete("tab"); else next.set("tab", t);
    setSp(next, { replace: true });
  };

  // Last run shown on the Run tab (live update at end of /run).
  const [lastRun, setLastRun] = useState<RunOutcome | null>(null);

  // Reset local state whenever a different flow loads.
  useEffect(() => {
    if (!data) return;
    setName(data.name);
    setDocs(data.docs ?? "");
    setSteps(data.steps ?? []);
    setSelectedStepId((data.steps ?? [])[0]?.id ?? null);
    setSavedAt(data.updated_at);
    setLastRun(null);
  }, [data?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!name) return;
    const prev = document.title;
    document.title = `${name} · API Stash`;
    return () => { document.title = prev; };
  }, [name]);

  const isDirty = useMemo(() => {
    if (!data) return false;
    return (
      data.name !== name ||
      (data.docs ?? "") !== docs ||
      JSON.stringify(data.steps) !== JSON.stringify(steps)
    );
  }, [data, name, docs, steps]);

  const save = useMutation({
    mutationFn: () => api.flows.update(flowId, { name, docs, steps }),
    onSuccess: () => {
      setSavedAt(new Date().toISOString());
      qc.invalidateQueries({ queryKey: ["flow", flowId] });
      if (data?.collection_id) qc.invalidateQueries({ queryKey: ["flows", data.collection_id] });
    },
  });

  const envId = sp.get("env") || envsQ.data?.environments.find((e) => e.is_default)?.id || "";

  const run = useMutation({
    mutationFn: async () => {
      if (isDirty) await save.mutateAsync();
      return api.flows.run(flowId, { environmentId: envId || undefined });
    },
    onSuccess: (r) => {
      setLastRun(r);
      qc.invalidateQueries({ queryKey: ["flow-runs", flowId] });
    },
  });

  const remove = useMutation({
    mutationFn: () => api.flows.remove(flowId),
    onSuccess: () => {
      if (data?.collection_id) qc.invalidateQueries({ queryKey: ["flows", data.collection_id] });
      const { search, nextActive } = closeOpenTabFor(workspaceId, `f:${flowId}`);
      const path = nextActive
        ? (nextActive.startsWith("w:") ? `/w/${nextActive.slice(2)}`
          : nextActive.startsWith("f:") ? `/f/${nextActive.slice(2)}`
          : nextActive.startsWith("r:") ? `/r/${nextActive.slice(2)}`
          : `/r/${nextActive}`)
        : "/";
      window.location.assign(path + search);
    },
  });

  const { size: stepsWidth, dragProps: stepsDrag } = useResizable({
    axis: "x", initial: 300, min: 200, max: 520, storageKey: "rest.flow.stepsWidth",
  });

  if (detailQ.isLoading) return <div className="grid h-full place-items-center"><InlineLoading label="Loading flow…" /></div>;
  if (!data) return <div className="grid h-full place-items-center muted">Flow not found.</div>;

  const selectedStep = steps.find((s) => s.id === selectedStepId) ?? null;
  const selectedStepIdx = selectedStep ? steps.indexOf(selectedStep) : -1;

  // Build a quick map of step-status from the last run so the steps
  // pane can show a tiny status pill next to each row.
  const lastRunStatusByStepId: Record<string, FlowRunStep | undefined> = {};
  for (const s of lastRun?.steps ?? []) {
    lastRunStatusByStepId[s.step_id] = s;
  }

  const replaceStep = (next: FlowStep) => {
    setSteps((prev) => prev.map((s) => (s.id === next.id ? next : s)));
  };
  const deleteStep = (id: string) => {
    setSteps((prev) => {
      const next = prev.filter((s) => s.id !== id);
      if (selectedStepId === id) setSelectedStepId(next[0]?.id ?? null);
      return next;
    });
  };
  const addStep = (kind: FlowStep["kind"]) => {
    const id = newStepId();
    let step: FlowStep;
    if (kind === "request") step = { id, kind, name: "New request", source: "saved", onFailure: "stop" };
    else if (kind === "script") step = { id, kind, name: "New script", source: "// ctx, env, steps, log\n" };
    else step = { id, kind: "delay", name: "Delay", ms: 500 };
    setSteps((prev) => [...prev, step]);
    setSelectedStepId(id);
    setTab("step");
  };

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="border-b border-border bg-surface px-3 py-1.5 flex items-center gap-2">
        <div
          className="flex-1 min-w-0 truncate text-sm font-medium text-text"
          title={name || "Untitled flow"}
        >{name || "Untitled flow"}</div>
        {isDirty && <span className="chip bg-warn/15 text-warn">Unsaved</span>}
        {!isDirty && savedAt && <span className="text-xs muted-strong">Saved {timeAgo(savedAt)}</span>}
        <LoadingButton className="btn" pending={save.isPending} disabled={!isDirty} onClick={() => save.mutate()}>Save</LoadingButton>
        <LoadingButton
          className="btn btn-primary"
          pending={run.isPending}
          disabled={steps.filter((s) => !s.disabled).length === 0}
          onClick={() => { setTab("run"); run.mutate(); }}
        >Run</LoadingButton>
        <button
          type="button"
          className="btn"
          onClick={async () => {
            const ok = await confirm({ title: `Delete "${data.name}"?`, description: "This permanently removes the flow and every run.", danger: true });
            if (ok) remove.mutate();
          }}
        >Delete</button>
      </div>

      <div className="flex-1 min-h-0 flex">
        {/* Left: steps list. */}
        <aside className="shrink-0 border-r border-border bg-surface flex flex-col min-h-0" style={{ width: stepsWidth }}>
          <div className="h-9 px-3 flex items-center justify-between border-b border-border">
            <div className="text-xs uppercase tracking-wider muted-strong font-semibold">Steps · {steps.length}</div>
            <AddStepMenu onAdd={addStep} />
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto scroll-pane">
            <StepsList
              steps={steps}
              selectedStepId={selectedStepId}
              onSelect={(id) => { setSelectedStepId(id); setTab("step"); }}
              onReorder={(next) => setSteps(next)}
              onToggle={(id) => setSteps((prev) => prev.map((s) => s.id === id ? { ...s, disabled: !s.disabled } : s))}
              onDelete={deleteStep}
              statusByStepId={lastRunStatusByStepId}
            />
          </div>
        </aside>
        <div {...stepsDrag} className="w-1.5 cursor-col-resize bg-transparent hover:bg-accent active:bg-accent-bright transition-colors shrink-0" aria-label="Resize steps pane" />

        {/* Right: detail pane. */}
        <div className="flex-1 min-w-0 flex flex-col min-h-0">
          <div className="border-b border-border bg-surface px-3 flex items-center gap-2 overflow-x-auto">
            <Tabs current={tab} onChange={setTab} />
            <div className="ml-auto shrink-0 flex items-center gap-2 py-1">
              <EnvPicker value={envId} onChange={(v) => {
                const next = new URLSearchParams(sp);
                if (v) next.set("env", v); else next.delete("env");
                setSp(next, { replace: true });
              }} envs={envsQ.data?.environments ?? []} />
            </div>
          </div>

          <div className="flex-1 min-h-0 overflow-auto scroll-pane p-3">
            {tab === "step" && (
              selectedStep ? (
                <StepEditor
                  workspaceId={workspaceId}
                  collectionId={data.collection_id}
                  step={selectedStep}
                  stepIndex={selectedStepIdx}
                  onChange={replaceStep}
                  lastRunStep={lastRunStatusByStepId[selectedStep.id]}
                />
              ) : (
                <div className="empty-card">No step selected. Add one from the <span className="empty-card-strong">+</span> menu in the left pane.</div>
              )
            )}
            {tab === "run" && (
              <RunPane outcome={lastRun} pending={run.isPending} error={run.error ? String((run.error as Error).message) : null} steps={steps} />
            )}
            {tab === "history" && (
              <HistoryPane flowId={flowId} onOpen={(r) => { setLastRun(r); setTab("run"); }} />
            )}
            {tab === "docs" && (
              <DocsEditor docs={docs} setDocs={setDocs} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Steps list ───────────────────────────────────────────────────────────

function StepsList({ steps, selectedStepId, onSelect, onReorder, onToggle, onDelete, statusByStepId }: {
  steps: FlowStep[];
  selectedStepId: string | null;
  onSelect: (id: string) => void;
  onReorder: (next: FlowStep[]) => void;
  onToggle: (id: string) => void;
  onDelete: (id: string) => void;
  statusByStepId: Record<string, FlowRunStep | undefined>;
}) {
  const ids = useMemo(() => steps.map((s) => s.id), [steps]);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const onDragEnd = (e: DragEndEvent) => {
    if (!e.over || e.active.id === e.over.id) return;
    const from = ids.indexOf(String(e.active.id));
    const to = ids.indexOf(String(e.over.id));
    if (from < 0 || to < 0) return;
    onReorder(arrayMove(steps, from, to));
  };
  if (steps.length === 0) {
    return <div className="p-3"><div className="empty-card">No steps yet. Use <span className="empty-card-strong">+</span> above.</div></div>;
  }
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <ol className="py-1">
          {steps.map((s, i) => (
            <SortableStepRow
              key={s.id}
              index={i + 1}
              step={s}
              selected={s.id === selectedStepId}
              status={statusByStepId[s.id]}
              onSelect={() => onSelect(s.id)}
              onToggle={() => onToggle(s.id)}
              onDelete={() => onDelete(s.id)}
            />
          ))}
        </ol>
      </SortableContext>
    </DndContext>
  );
}

function SortableStepRow({ step, index, selected, status, onSelect, onToggle, onDelete }: {
  step: FlowStep;
  index: number;
  selected: boolean;
  status: FlowRunStep | undefined;
  onSelect: () => void;
  onToggle: () => void;
  onDelete: () => void;
}) {
  const { setNodeRef, attributes, listeners, transform, transition, isDragging } = useSortable({ id: step.id });
  const style: React.CSSProperties = {
    transform: transform ? `translate3d(0, ${transform.y}px, 0)` : undefined,
    transition,
    opacity: isDragging ? 0.6 : step.disabled ? 0.5 : undefined,
  };
  return (
    <li
      ref={setNodeRef}
      style={style}
      className={
        "group flex items-center gap-2 px-2 py-1.5 mx-1 rounded cursor-pointer min-w-0 " +
        (selected ? "bg-accent-soft text-accent-bright" : "hover:bg-surface-2 text-text")
      }
      onClick={onSelect}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="shrink-0 text-text-faint hover:text-text cursor-grab active:cursor-grabbing"
        title="Drag to reorder"
        onClick={(e) => e.stopPropagation()}
      >⋮⋮</button>
      <span className="chip bg-surface-3 muted-strong mono shrink-0 text-[10px]">{index}</span>
      <StepKindIcon kind={step.kind} />
      <span className="flex-1 min-w-0 truncate text-sm">{step.name}</span>
      {status && <StepStatusDot status={status.status} />}
      <button
        type="button"
        className="h-5 w-5 grid place-items-center rounded text-text-faint opacity-0 group-hover:opacity-100 hover:text-text hover:bg-surface-3"
        title={step.disabled ? "Enable step" : "Disable step"}
        onClick={(e) => { e.stopPropagation(); onToggle(); }}
      >{step.disabled ? "○" : "●"}</button>
      <button
        type="button"
        className="h-5 w-5 grid place-items-center rounded text-text-faint opacity-0 group-hover:opacity-100 hover:text-danger hover:bg-surface-3"
        title="Delete step"
        onClick={(e) => { e.stopPropagation(); onDelete(); }}
      >×</button>
    </li>
  );
}

function StepKindIcon({ kind }: { kind: FlowStep["kind"] }) {
  if (kind === "request") return <span className="chip bg-accent-soft text-accent shrink-0 text-[10px]">REQ</span>;
  if (kind === "script") return <span className="chip bg-warn/20 text-warn shrink-0 text-[10px]">JS</span>;
  return <span className="chip bg-surface-3 muted-strong shrink-0 text-[10px]">WAIT</span>;
}

function StepStatusDot({ status }: { status: FlowRunStep["status"] }) {
  let cls = "bg-text-faint";
  let title = status;
  if (status === "succeeded") cls = "bg-good";
  else if (status === "failed") cls = "bg-danger";
  else if (status === "running") cls = "bg-accent";
  else if (status === "skipped") cls = "bg-text-faint";
  return <span className={"shrink-0 w-2 h-2 rounded-full " + cls} title={title} />;
}

// ── Tabs ────────────────────────────────────────────────────────────────

function Tabs({ current, onChange }: { current: Tab; onChange: (t: Tab) => void }) {
  const items: Array<{ id: Tab; label: string }> = [
    { id: "step", label: "Step" },
    { id: "run", label: "Run" },
    { id: "history", label: "History" },
    { id: "docs", label: "Docs" },
  ];
  return (
    <div className="flex gap-1">
      {items.map((t) => {
        const active = t.id === current;
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => onChange(t.id)}
            className={
              "px-3 py-2 text-sm font-medium border-b-2 -mb-px " +
              (active ? "border-accent text-text" : "border-transparent text-text-dim hover:text-text")
            }
          >{t.label}</button>
        );
      })}
    </div>
  );
}

// ── Step editor (router by kind) ────────────────────────────────────────

function StepEditor({ workspaceId, collectionId, step, stepIndex, onChange, lastRunStep }: {
  workspaceId: string;
  collectionId: string;
  step: FlowStep;
  stepIndex: number;
  onChange: (s: FlowStep) => void;
  lastRunStep: FlowRunStep | undefined;
}) {
  return (
    <div className="space-y-3 min-w-0">
      <div className="flex items-center gap-2">
        <span className="chip bg-surface-3 muted-strong mono text-[10px]">{stepIndex + 1}</span>
        <input
          className="input flex-1 !text-sm"
          value={step.name}
          onChange={(e) => onChange({ ...step, name: e.target.value })}
          placeholder="Step name"
          aria-label="Step name"
        />
        <label className="flex items-center gap-2 text-xs muted-strong">
          <input
            type="checkbox"
            checked={!step.disabled}
            onChange={(e) => onChange({ ...step, disabled: !e.target.checked })}
          /> Enabled
        </label>
      </div>
      {step.kind === "request" && (
        <RequestStepEditor
          workspaceId={workspaceId}
          collectionId={collectionId}
          step={step}
          onChange={onChange}
          lastRunStep={lastRunStep}
        />
      )}
      {step.kind === "script" && (
        <ScriptStepEditor step={step} onChange={onChange} />
      )}
      {step.kind === "delay" && (
        <DelayStepEditor step={step} onChange={onChange} />
      )}
    </div>
  );
}

// ── Request step editor ─────────────────────────────────────────────────

function RequestStepEditor({ workspaceId, collectionId, step, onChange, lastRunStep }: {
  workspaceId: string;
  collectionId: string;
  step: Extract<FlowStep, { kind: "request" }>;
  onChange: (s: FlowStep) => void;
  lastRunStep: FlowRunStep | undefined;
}) {
  void workspaceId;
  const requestsQ = useQuery({
    queryKey: ["requests", collectionId],
    queryFn: () => api.collections.requests(collectionId),
  });
  const allRequests: RequestSummary[] = requestsQ.data?.requests ?? [];
  const selectedSavedReq = step.requestId ? allRequests.find((r) => r.id === step.requestId) : null;

  return (
    <div className="space-y-3 min-w-0">
      <div className="flex items-center gap-3">
        <label className="flex items-center gap-2 text-xs muted-strong">
          <span className="label">Source</span>
          <select
            value={step.source}
            onChange={(e) => onChange({ ...step, source: e.target.value as "saved" | "inline" })}
            className="input py-1 text-sm"
          >
            <option value="saved">Saved request</option>
            <option value="inline">Inline</option>
          </select>
        </label>
      </div>

      {step.source === "saved" ? (
        <div className="space-y-2">
          <select
            className="input w-full !text-sm"
            value={step.requestId ?? ""}
            onChange={(e) => onChange({ ...step, requestId: e.target.value || undefined })}
          >
            <option value="">— Pick a request —</option>
            {allRequests.map((r) => (
              <option key={r.id} value={r.id}>{r.method} · {r.name}</option>
            ))}
          </select>
          {selectedSavedReq && (
            <div className="text-xs muted truncate" title={selectedSavedReq.url}>
              <span className={"method-tag " + selectedSavedReq.method.toLowerCase()}>{selectedSavedReq.method}</span>{" "}
              <span className="mono">{selectedSavedReq.url}</span>
            </div>
          )}
          {/* For saved requests we only expose URL/method overrides for
              now. Headers/query/body overrides land in a future pass —
              the underlying spec already supports them via `overrides`. */}
          <div className="grid grid-cols-2 gap-2">
            <input
              className="input !text-sm"
              placeholder="Override method"
              value={step.overrides?.method ?? ""}
              onChange={(e) => onChange({ ...step, overrides: { ...(step.overrides ?? {}), method: e.target.value || undefined } })}
            />
            <input
              className="input !text-sm"
              placeholder="Override URL"
              value={step.overrides?.url ?? ""}
              onChange={(e) => onChange({ ...step, overrides: { ...(step.overrides ?? {}), url: e.target.value || undefined } })}
            />
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <div className="grid grid-cols-[120px_minmax(0,1fr)] gap-2">
            <select
              className="input !text-sm"
              value={step.method ?? "GET"}
              onChange={(e) => onChange({ ...step, method: e.target.value })}
            >
              {["GET", "POST", "PUT", "PATCH", "DELETE", "HEAD", "OPTIONS"].map((m) =>
                <option key={m} value={m}>{m}</option>)}
            </select>
            <input
              className="input !text-sm"
              placeholder="https://api.example.com/users"
              value={step.url ?? ""}
              onChange={(e) => onChange({ ...step, url: e.target.value })}
            />
          </div>
          <select
            className="input !text-sm"
            value={step.body_type ?? "none"}
            onChange={(e) => onChange({ ...step, body_type: e.target.value as Extract<FlowStep, { kind: "request" }>["body_type"] })}
          >
            <option value="none">No body</option>
            <option value="json">JSON</option>
            <option value="text">Text</option>
            <option value="urlencoded">Form URL Encoded</option>
          </select>
          {(step.body_type ?? "none") !== "none" && (
            <textarea
              className="input input-mono w-full min-h-[120px] text-xs"
              placeholder={step.body_type === "json" ? '{ "key": "{{token}}" }' : "Body"}
              value={step.body ?? ""}
              onChange={(e) => onChange({ ...step, body: e.target.value })}
              spellCheck={false}
            />
          )}
        </div>
      )}

      {/* Extract section. */}
      <div className="space-y-1">
        <div className="text-xs uppercase tracking-wider muted-strong font-semibold pt-2">Extract</div>
        <p className="text-[11px] muted">
          Pluck values out of the response into <span className="mono">ctx</span> for later steps. Paths look like
          <span className="mono"> $.body.token</span>, <span className="mono">$.body[0].id</span>, or <span className="mono">$.headers.x-id</span>.
        </p>
        <ExtractEditor
          rows={step.extract ?? []}
          onChange={(rows) => onChange({ ...step, extract: rows })}
          lastRunStep={lastRunStep}
        />
      </div>

      {/* Expect + onFailure. */}
      <div className="grid grid-cols-2 gap-3">
        <label className="text-xs">
          <span className="label">Expect status</span>
          <input
            className="input !text-sm mt-1"
            placeholder="e.g. 2xx or 200,201"
            value={step.expect?.status ?? ""}
            onChange={(e) => onChange({ ...step, expect: { status: e.target.value || undefined } })}
          />
        </label>
        <label className="text-xs">
          <span className="label">On failure</span>
          <div className="flex gap-3 mt-1">
            {(["stop", "continue"] as const).map((v) => (
              <label key={v} className="flex items-center gap-1 text-sm">
                <input
                  type="radio"
                  checked={(step.onFailure ?? "stop") === v}
                  onChange={() => onChange({ ...step, onFailure: v })}
                /> {v}
              </label>
            ))}
          </div>
        </label>
      </div>
    </div>
  );
}

// Pluck-values-into-ctx editor. The "Picker" column scans the LAST run's
// response and lets the user click a leaf path to insert it directly.
function ExtractEditor({ rows, onChange, lastRunStep }: {
  rows: Array<{ as: string; from: string }>;
  onChange: (next: Array<{ as: string; from: string }>) => void;
  lastRunStep: FlowRunStep | undefined;
}) {
  const [pickerOpen, setPickerOpen] = useState<number | null>(null);
  const lastOutput = lastRunStep?.output as { stepCtx?: { body_json?: unknown; status?: number; headers?: Record<string, string> } } | undefined;
  const paths = useMemo(() => collectPaths(lastOutput?.stepCtx ?? null), [lastOutput?.stepCtx]);

  const set = (i: number, patch: Partial<{ as: string; from: string }>) =>
    onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const removeRow = (i: number) => onChange(rows.filter((_, idx) => idx !== i));
  const addRow = () => onChange([...rows, { as: "", from: "" }]);

  return (
    <div className="space-y-1 min-w-0">
      {rows.length > 0 && (
        <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,2fr)_60px_28px] gap-2 px-1 text-[11px] uppercase tracking-wider muted-strong">
          <div>As</div>
          <div>From</div>
          <div></div>
          <div></div>
        </div>
      )}
      {rows.map((r, i) => (
        <div key={i} className="grid grid-cols-[minmax(0,1fr)_minmax(0,2fr)_60px_28px] gap-2 items-center relative">
          <input
            className="input !text-sm font-mono"
            placeholder="token"
            value={r.as}
            onChange={(e) => set(i, { as: e.target.value })}
            spellCheck={false}
          />
          <input
            className="input !text-sm font-mono"
            placeholder="$.body.token"
            value={r.from}
            onChange={(e) => set(i, { from: e.target.value })}
            spellCheck={false}
          />
          <button
            type="button"
            className="btn !text-xs"
            disabled={paths.length === 0}
            onClick={() => setPickerOpen(pickerOpen === i ? null : i)}
            title={paths.length === 0 ? "Run the flow once to see paths" : "Pick a path from the last response"}
          >Pick</button>
          <button
            type="button"
            onClick={() => removeRow(i)}
            className="h-7 w-7 grid place-items-center rounded text-text-faint hover:text-danger hover:bg-surface-2"
            title="Remove"
          >✕</button>
          {pickerOpen === i && paths.length > 0 && (
            <div className="absolute z-20 right-12 top-full mt-1 card shadow-[var(--shadow-strong)] p-1 w-[420px] max-h-72 overflow-y-auto scroll-pane">
              <div className="px-2 py-1 text-[11px] muted-strong">Last response — click a path to insert</div>
              {paths.map((p) => (
                <button
                  key={p.path}
                  type="button"
                  className="w-full text-left px-2 py-1 rounded hover:bg-surface-2 text-xs flex items-center gap-2 min-w-0"
                  onMouseDown={(e) => { e.preventDefault(); set(i, { from: p.path }); setPickerOpen(null); }}
                >
                  <span className="font-mono truncate">{p.path}</span>
                  <span className="ml-auto muted text-[10px] truncate max-w-[180px]">{p.preview}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      ))}
      {rows.length === 0 && (
        <div className="empty-card">No extracts. Click <span className="empty-card-strong">+ Add extract</span> to pluck a value from the response.</div>
      )}
      <div className="pt-1">
        <button type="button" className="btn" onClick={addRow}>＋ Add extract</button>
      </div>
    </div>
  );
}

// Walks the step's last-run snapshot ({ body_json, status, headers }) and
// emits selectable JSONPath-ish strings + a short preview value. We cap
// the recursion depth + total path count so a huge response doesn't
// freeze the popover.
function collectPaths(stepCtx: { body_json?: unknown; status?: number; headers?: Record<string, string> } | null): Array<{ path: string; preview: string }> {
  if (!stepCtx) return [];
  const out: Array<{ path: string; preview: string }> = [];
  out.push({ path: "$.status", preview: String(stepCtx.status ?? "") });
  for (const [k, v] of Object.entries(stepCtx.headers ?? {})) {
    out.push({ path: `$.headers.${k}`, preview: String(v) });
  }
  walk(stepCtx.body_json ?? null, "$.body", 0);
  return out.slice(0, 200);

  function walk(v: unknown, path: string, depth: number) {
    if (out.length > 200 || depth > 6) return;
    if (v === null || typeof v !== "object") {
      out.push({ path, preview: previewVal(v) });
      return;
    }
    if (Array.isArray(v)) {
      out.push({ path, preview: `[${v.length}]` });
      v.slice(0, 8).forEach((item, idx) => walk(item, `${path}[${idx}]`, depth + 1));
      return;
    }
    out.push({ path, preview: "{…}" });
    let i = 0;
    for (const [k, vv] of Object.entries(v as Record<string, unknown>)) {
      if (i++ > 32) break;
      walk(vv, `${path}.${k}`, depth + 1);
    }
  }
}
function previewVal(v: unknown): string {
  if (v === null) return "null";
  if (typeof v === "string") return JSON.stringify(v).slice(0, 60);
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  return typeof v;
}

// ── Script step editor ──────────────────────────────────────────────────

function ScriptStepEditor({ step, onChange }: { step: Extract<FlowStep, { kind: "script" }>; onChange: (s: FlowStep) => void }) {
  const isDark = typeof document !== "undefined" && document.documentElement.classList.contains("dark");
  return (
    <div className="space-y-2 min-w-0">
      <div className="rounded border border-border bg-surface-2 p-2 text-[11px] muted-strong font-mono whitespace-pre">
{`// ctx: shared mutable state across steps
// env: read-only env vars
// steps: ctx.steps[stepId] = { status, headers, body, body_json, ok }
// log("hello"): append to step output
// Throw to fail the flow. Return false to halt successfully.`}
      </div>
      <div className="min-h-[260px]">
        <CodeMirror
          value={step.source}
          onChange={(v) => onChange({ ...step, source: v })}
          theme={isDark ? oneDark : githubLight}
          extensions={[javascript()]}
          height="260px"
          basicSetup={{
            lineNumbers: true, bracketMatching: true, closeBrackets: true,
            indentOnInput: true, autocompletion: false, foldGutter: false,
            highlightActiveLine: true,
          }}
          className="cm-host border border-border rounded"
        />
      </div>
      <p className="text-[11px] muted-strong">
        Scripts run synchronously inside the Worker. Soft timeout: 100ms (an infinite loop will still consume the
        Worker's CPU budget until the runtime kills it).
      </p>
    </div>
  );
}

// ── Delay step editor ───────────────────────────────────────────────────

function DelayStepEditor({ step, onChange }: { step: Extract<FlowStep, { kind: "delay" }>; onChange: (s: FlowStep) => void }) {
  return (
    <div className="space-y-2 min-w-0">
      <label className="text-xs">
        <span className="label">Wait (ms)</span>
        <input
          type="number"
          min={0}
          max={30000}
          step={50}
          className="input mt-1 !text-sm w-40"
          value={step.ms}
          onChange={(e) => onChange({ ...step, ms: Math.max(0, Math.min(30000, Math.trunc(Number(e.target.value) || 0))) })}
        />
      </label>
      <p className="text-[11px] muted">Capped at 30,000 ms.</p>
    </div>
  );
}

// ── Run pane ────────────────────────────────────────────────────────────

function RunPane({ outcome, pending, error, steps }: { outcome: RunOutcome | null; pending: boolean; error: string | null; steps: FlowStep[] }) {
  if (pending) {
    return <div className="grid h-full place-items-center"><InlineLoading label="Running flow…" /></div>;
  }
  if (!outcome && error) {
    return <div className="empty-card text-danger">Run failed: {error}</div>;
  }
  if (!outcome) {
    return (
      <div className="empty-card">
        Press <span className="empty-card-strong">Run</span> in the header to execute this flow. Results show up here.
      </div>
    );
  }
  return (
    <div className="space-y-3 min-w-0">
      <div className="flex items-center gap-3">
        <RunStatusPill status={outcome.run.status} />
        <span className="text-xs muted-strong">{outcome.run.duration_ms ?? 0} ms · {outcome.run.total_steps} steps</span>
        {outcome.run.error && <span className="text-xs text-danger truncate" title={outcome.run.error}>{outcome.run.error}</span>}
      </div>
      <ol className="space-y-2">
        {outcome.steps.map((s) => {
          const stepDef = steps.find((d) => d.id === s.step_id);
          return <RunStepCard key={s.id} step={s} disabled={!!stepDef?.disabled} />;
        })}
      </ol>
      {outcome.run.final_context != null && (
        <div className="space-y-1">
          <div className="text-xs uppercase tracking-wider muted-strong font-semibold">Final ctx</div>
          <CodeViewer value={JSON.stringify(outcome.run.final_context, null, 2)} lang="json" maxHeight="320px" />
        </div>
      )}
    </div>
  );
}

function RunStepCard({ step, disabled }: { step: FlowRunStep; disabled: boolean }) {
  const [open, setOpen] = useState(step.status === "failed");
  return (
    <li className="rounded border border-border bg-surface-2/40">
      <button
        type="button"
        className="w-full flex items-center gap-3 px-3 py-2 text-left min-w-0"
        onClick={() => setOpen((v) => !v)}
      >
        <span className="chip bg-surface-3 muted-strong mono shrink-0 text-[10px]">{step.step_index + 1}</span>
        <StepKindIcon kind={step.step_kind as FlowStep["kind"]} />
        <span className="text-sm flex-1 min-w-0 truncate">{step.step_name}</span>
        {disabled && <span className="chip bg-surface-3 muted-strong text-[10px]">disabled</span>}
        <span className="text-xs muted-strong shrink-0">{step.duration_ms ?? 0} ms</span>
        <RunStepStatusPill status={step.status} />
      </button>
      {open && (
        <div className="border-t border-border px-3 py-2 space-y-2">
          {step.error && <div className="text-xs text-danger font-mono break-all">{step.error}</div>}
          {step.output != null && (
            <div className="space-y-1">
              <div className="text-[11px] uppercase tracking-wider muted-strong">Output</div>
              <CodeViewer value={JSON.stringify(step.output, null, 2)} lang="json" maxHeight="240px" />
            </div>
          )}
        </div>
      )}
    </li>
  );
}

function RunStatusPill({ status }: { status: FlowRun["status"] }) {
  const map: Record<FlowRun["status"], string> = {
    running: "bg-accent-soft text-accent",
    succeeded: "bg-good/15 text-good",
    failed: "bg-danger/15 text-danger",
    cancelled: "bg-surface-3 muted-strong",
  };
  return <span className={"chip " + map[status]}>{status}</span>;
}
function RunStepStatusPill({ status }: { status: FlowRunStep["status"] }) {
  const map: Record<FlowRunStep["status"], string> = {
    pending: "bg-surface-3 muted-strong",
    running: "bg-accent-soft text-accent",
    succeeded: "bg-good/15 text-good",
    failed: "bg-danger/15 text-danger",
    skipped: "bg-surface-3 muted-strong",
  };
  return <span className={"chip text-[10px] " + map[status]}>{status}</span>;
}

// ── History pane ────────────────────────────────────────────────────────

function HistoryPane({ flowId, onOpen }: { flowId: string; onOpen: (r: RunOutcome) => void }) {
  const runsQ = useQuery({ queryKey: ["flow-runs", flowId], queryFn: () => api.flows.runs(flowId, 50) });
  const [loadingId, setLoadingId] = useState<string | null>(null);
  if (runsQ.isLoading) return <InlineLoading label="Loading history…" />;
  const runs = runsQ.data?.runs ?? [];
  if (runs.length === 0) return <div className="empty-card">No runs yet. Press <span className="empty-card-strong">Run</span> to execute.</div>;
  return (
    <ul className="space-y-1">
      {runs.map((r) => (
        <li key={r.id}>
          <button
            type="button"
            className="w-full flex items-center gap-3 px-2 py-1.5 rounded hover:bg-surface-2 text-left min-w-0"
            onClick={async () => {
              setLoadingId(r.id);
              try {
                const full = await api.flows.runDetail(flowId, r.id);
                onOpen(full);
              } finally {
                setLoadingId(null);
              }
            }}
          >
            <RunStatusPill status={r.status} />
            <span className="text-xs muted-strong">{new Date(r.started_at).toLocaleString()}</span>
            <span className="text-xs muted ml-auto shrink-0">{r.duration_ms ?? 0} ms · {r.total_steps} steps</span>
            {loadingId === r.id && <InlineLoading />}
          </button>
        </li>
      ))}
    </ul>
  );
}

// ── Docs ────────────────────────────────────────────────────────────────

function DocsEditor({ docs, setDocs }: { docs: string; setDocs: (s: string) => void }) {
  const [mode, setMode] = useState<"edit" | "preview" | "split">("split");
  return (
    <div className="space-y-3 h-full flex flex-col min-w-0">
      <div className="flex items-center gap-2 flex-wrap">
        <label className="label">Docs (Markdown)</label>
        <div className="ml-auto flex gap-1">
          {(["edit", "split", "preview"] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              className={
                "px-2 py-0.5 rounded-sm text-xs font-medium " +
                (m === mode ? "bg-accent text-[var(--ink-on-accent)]" : "bg-surface-2 text-text-dim hover:bg-surface-3 hover:text-text")
              }
            >{m}</button>
          ))}
        </div>
      </div>
      <div className={"flex-1 min-h-[240px] min-w-0 grid gap-3 " + (mode === "split" ? "grid-cols-1 lg:grid-cols-2" : "grid-cols-1")}>
        {mode !== "preview" && (
          <textarea
            className="input input-mono min-h-[240px] min-w-0 w-full"
            value={docs}
            onChange={(e) => setDocs(e.target.value)}
            placeholder="Document what this flow does."
            spellCheck={false}
          />
        )}
        {mode !== "edit" && (
          <div className="rounded border border-border bg-surface-2 p-3 overflow-auto scroll-pane min-h-[240px] min-w-0">
            {docs ? <Markdown source={docs} /> : <p className="text-sm muted italic">Nothing to preview yet.</p>}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Add-step menu ───────────────────────────────────────────────────────

function AddStepMenu({ onAdd }: { onAdd: (kind: FlowStep["kind"]) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        title="Add step"
        aria-label="Add step"
        onClick={() => setOpen((v) => !v)}
        onBlur={() => setTimeout(() => setOpen(false), 120)}
        className="h-6 w-6 grid place-items-center rounded text-text-faint hover:text-text hover:bg-surface-2"
      >＋</button>
      {open && (
        <div className="absolute right-0 top-full mt-1 card shadow-[var(--shadow-strong)] p-1 z-40 text-sm w-44" role="menu">
          <button className="w-full text-left px-2 py-1 rounded hover:bg-surface-2" onMouseDown={(e) => { e.preventDefault(); onAdd("request"); setOpen(false); }}>Add request step</button>
          <button className="w-full text-left px-2 py-1 rounded hover:bg-surface-2" onMouseDown={(e) => { e.preventDefault(); onAdd("script"); setOpen(false); }}>Add script step</button>
          <button className="w-full text-left px-2 py-1 rounded hover:bg-surface-2" onMouseDown={(e) => { e.preventDefault(); onAdd("delay"); setOpen(false); }}>Add delay step</button>
        </div>
      )}
    </div>
  );
}

// ── Env picker (simple select) ──────────────────────────────────────────

function EnvPicker({ value, onChange, envs }: { value: string; onChange: (v: string) => void; envs: Array<{ id: string; name: string }> }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="input !text-xs py-1 max-w-[180px]"
      aria-label="Environment"
    >
      <option value="">No environment</option>
      {envs.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
    </select>
  );
}

// ── Misc ────────────────────────────────────────────────────────────────

function newStepId(): string {
  // Browser-side equivalent of the server's id helper — short random
  // suffix so collisions inside a single flow are astronomically rare.
  // We only need uniqueness within a flow's step list.
  const arr = new Uint8Array(8);
  crypto.getRandomValues(arr);
  return "stp_" + Array.from(arr, (b) => b.toString(16).padStart(2, "0")).join("");
}

function timeAgo(iso: string): string {
  const t = new Date(iso).getTime();
  const diff = Date.now() - t;
  if (diff < 60_000) return "just now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return new Date(iso).toLocaleDateString();
}
